import { HttpTransport } from '@estee/elc-http-transport';
import { IGetPayload, IPayload } from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { OrderNumberType } from '../internal/domain/entities/Config';
import { DEFAULT_ORDER_PATH, OrderTrackingSearchParam } from '../internal/utils/Constants';
import { PickupStoreInfo } from 'src/internal/domain/entities/OrderInfo';

export interface IEstmimatedDate {
    estimated_date: string;
    guaranteed_date: string;
}

export interface IItemInfo {
    item_id: string;
    sku: string;
}

export interface IOrderNarvarItem {
    item_id: string;
    sku: string;
    name: string;
    quantity: string;
    sku_image: string;
    sku_url: string;
    fulfillment_status: string;
    size: string;
}

export interface IOrderNarvarShipment {
    carrier_moniker: string;
    tracking_number: string;
    status: string;
    status_code?: string;
    delivery: IEstmimatedDate;
    items_info: IItemInfo[];
    status_timestamp: string;
}

export interface IOrderNarvarReturn {
    return_creation_date: string;
    return_tracking_url: string;
    return_items: IItemInfo[];
    shipments: IOrderNarvarShipment[];
}

export interface INarvarOrderInfo {
    order_number: string;
    order_date: string;
    order_items: IOrderNarvarItem[];
    shipments: IOrderNarvarShipment[];
    returns?: IOrderNarvarReturn[];
}

export interface INarvarOrderData {
    order_info: INarvarOrderInfo;
}

export interface IOrderApiResponse {
    trans_id: number;
    trans_order_id: number;
    order_date: string;
    order_status: number;
    delivery_method: number;
    delivered_date: string;
    carrier: string;
    shipped_date: string;
    tracking_number: string;
    narvar_data: string | INarvarOrderData;
    // As the BE API has used narvar_ship_method as the service parameter consumed in tracking url
    // in future if more services added e.g. fedex_ship_method, another_company_ship_method
    // we need to refactor this parameter name to a generic one
    narvar_ship_method?: string;
    shipments?: IShipment[];
    pickup_store_data?: PickupStoreInfo;
    final_sale: string;
}

export interface IShipment {
    is_complete: boolean;
    is_return: boolean;
    carrier: string;
    shipment_tracking_number: string;
    shipment_status: string;
    last_updated_date: string;
    shipped_date: string;
    estimated_delivery_date: string;
    delivered_date: string;
    items: IShipmentItem[];
    return_tracking_url?: string;
}

export interface IShipmentItem {
    name: string;
    subheading: string;
    quantity: string;
    sku_id: string;
    sku_image: string;
    sku_url: string | null;
    size: string | null | undefined;
    hex_value: string | null;
    shade_name: string | null;
    sku_type: string | null;
    is_free_promo: number;
}

export enum OrderApiResponseCode {
    SUCCESS = 'success',
    NOT_FOUND = 'notFound',
    INVALID_ORDER_NUMBER = 'invalidOrderNumber',
    GENERIC_ERROR = 'genericError'
}

export interface IOrderApiSdkResponse {
    data?: IOrderApiResponse;
    code: OrderApiResponseCode;
}

export interface IGlobalHeaders {
    Authorization: string;
    AuthorizationCredentials: string;
}

export type EncryptedOrderNumberDataType = { data: string; token: string };

export interface IOrderTrackingApiConfig {
    regionId: string;
    brandId: string;
    token: string;
    baseUrl: string;
    orderNumberType: OrderNumberType | undefined;
}

export class OrderTrackingApiSdk {
    httpTransport: HttpTransport;
    logger: ELCLogger;
    brandRegionQuery: string = '';
    orderNumberType: OrderNumberType | undefined;
    orderPath = DEFAULT_ORDER_PATH;

    constructor(logger: ELCLogger, httpTransport: HttpTransport) {
        this.httpTransport = httpTransport;
        this.logger = logger;
    }

    private getGlobalHeaders = (token: string): IGlobalHeaders => {
        const AuthorizationCredentials = `Basic ${token}`;
        const Authorization = AuthorizationCredentials;

        // add the header authorization twice for ios support: APACFTRS1-210
        return {
            Authorization,
            AuthorizationCredentials
        };
    };

    public setup(config: IOrderTrackingApiConfig): void {
        const { baseUrl, token, regionId, brandId, orderNumberType } = config;
        const query = new URLSearchParams();
        query.set(OrderTrackingSearchParam.REGION, regionId);
        query.set(OrderTrackingSearchParam.BRAND, brandId);
        this.brandRegionQuery = query.toString();
        this.orderNumberType = orderNumberType;
        this.httpTransport.setBaseUrl(baseUrl);
        this.httpTransport.addGlobalHeaders(this.getGlobalHeaders(token));
    }

    public getOrderTracking = async (
        query: string | EncryptedOrderNumberDataType
    ): Promise<IOrderApiSdkResponse> => {
        let url;
        try {
            let response;
            const options = {
                credentials: 'include'
            };
            if (typeof query === 'string') {
                url = `${this.orderPath}/${this.orderNumberType}/${query}?${this.brandRegionQuery}`;
                response = await this.httpTransport.get({
                    options,
                    url
                } as IGetPayload);
            } else {
                url = `${this.orderPath}/e${this.orderNumberType}?${this.brandRegionQuery}`;
                response = await this.httpTransport.post({
                    options,
                    url,
                    body: query
                } as IPayload);
            }

            return response?.data
                ? { data: response.data, code: OrderApiResponseCode.SUCCESS }
                : { code: OrderApiResponseCode.GENERIC_ERROR };
        } catch (e) {
            this.logger.error({
                message: `Unable to get order tracking info from ${url}`,
                triggerType: TriggerType.api
            });

            const code =
                e.errorResponse?.data?.status === 404
                    ? OrderApiResponseCode.NOT_FOUND
                    : OrderApiResponseCode.GENERIC_ERROR;

            return { code };
        }
    };
}
